// Pages/Bubbles/BubbleChatPage.jsx
import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQuery } from 'react-query';
import API from '../API';
import BubbleChatScreen from '../Components/ChatComponents/BubbleChatScreen';
import useAuth from '../Hooks/use-auth';
import { useCreatorAuth } from '../Hooks/creator-use-auth';
import routes from '../Config/routes';
import PageTitle from '../Components/PageTitle';

const NAV_BAR_HEIGHT = 86;

export default function BubbleChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();

  // Fetch conversation metadata to get bubble ID
  const {
    data: metadata,
    isLoading: metadataLoading,
    error: metadataError
  } = useQuery(
    ["bubble_conversation_metadata", conversationId],
    () => API.conversations.getMetadata(conversationId),
    {
      enabled: !!conversationId,
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
    }
  );

  const bubbleId = metadata?.conversation?.bubble_id || metadata?.bubble?.id;
  const bubbleName = metadata?.conversation?.bubble_name || metadata?.bubble?.name;

  const handleNavigateBack = () => {
    const returnTo = location.state?.returnTo;
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(routes.conversationshub);
    }
  };

  // Show loading state
  if (metadataLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: `calc(100vh - ${NAV_BAR_HEIGHT}px)`,
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="body1" color="text.secondary">
          Loading bubble chat...
        </Typography>
      </Box>
    );
  }

  // Show error state
  if (metadataError || !bubbleId) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: `calc(100vh - ${NAV_BAR_HEIGHT}px)`,
          gap: 2,
          p: 3
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Failed to load bubble chat
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {metadataError?.response?.data?.error || metadataError?.message || 'Bubble not found'}
        </Typography>
        <Button
          variant="contained"
          onClick={handleNavigateBack}
          startIcon={<ArrowBackIcon />}
        >
          Back to Conversations
        </Button>
      </Box>
    );
  }

  return (
    <>
      <PageTitle title={`${bubbleName || 'Bubble'} - Chat`} />
      <Box
        sx={{
          height: `calc(100vh - ${NAV_BAR_HEIGHT}px)`,
          width: '100%',
          display: 'flex',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <BubbleChatScreen
          conversationId={parseInt(conversationId)}
          bubbleId={bubbleId}
          onNavigateBack={handleNavigateBack}
          sx={{
            height: '100%',
            width: '100%',
          }}
        />
      </Box>
    </>
  );
}